import ReactGA from 'react-ga4';
import { safeLocalStorage } from './safeStorage';

let isInitialized = false;
let currentActiveMeasurementId = '';

export const getGAMeasurementId = (): string => {
  if (typeof window !== 'undefined' && window.GA_MEASUREMENT_ID) {
    return window.GA_MEASUREMENT_ID.trim();
  }
  const fromStorage = safeLocalStorage.getItem('GA_MEASUREMENT_ID');
  if (fromStorage && fromStorage.trim()) {
    return fromStorage.trim();
  }
  const fromVite = import.meta.env && import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (fromVite && typeof fromVite === 'string' && fromVite.trim()) {
    return fromVite.trim();
  }
  return 'G-XXXXXXXXXX';
};

export const isRealGAMeasurementId = (id?: string): boolean => {
  const currentId = id || getGAMeasurementId();
  return Boolean(
    currentId &&
    currentId !== 'G-XXXXXXXXXX' &&
    /^G-[A-Z0-9]+$/i.test(currentId.trim())
  );
};

const initConsentModeDefault = (): void => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer?.push(arguments);
    };
  }

  let hasConsent = false;
  try {
    const rawConsent = safeLocalStorage.getItem('carp_arena_cookie_consent');
    if (rawConsent) {
      const parsed = JSON.parse(rawConsent);
      if (parsed.status === 'accepted') hasConsent = true;
    }
  } catch {
    // ignore
  }

  window.gtag('consent', 'default', {
    analytics_storage: hasConsent ? 'granted' : 'denied',
    ad_storage: hasConsent ? 'granted' : 'denied',
    ad_user_data: hasConsent ? 'granted' : 'denied',
    ad_personalization: hasConsent ? 'granted' : 'denied',
  });
};

export const initGA = (forceReinit = false): void => {
  if (typeof window === 'undefined') return;

  const measurementId = getGAMeasurementId();
  const isValid = isRealGAMeasurementId(measurementId);

  if (isInitialized && !forceReinit && currentActiveMeasurementId === measurementId) {
    return;
  }

  initConsentModeDefault();

  if (isValid) {
    try {
      ReactGA.initialize(measurementId, {
        testMode: false,
        gaOptions: { send_page_view: false },
      });
      isInitialized = true;
      currentActiveMeasurementId = measurementId;
      console.log(`✅ [GA4] Google Analytics 4 (react-ga4) инициализирован: ${measurementId}`);
    } catch (err) {
      console.warn('[GA4] Ошибка инициализации ReactGA:', err);
    }
  } else {
    try {
      ReactGA.initialize('G-XXXXXXXXXX', { testMode: true });
      isInitialized = true;
      currentActiveMeasurementId = 'G-XXXXXXXXXX';
    } catch {
      // ignore
    }
  }
};

export const setGAMeasurementId = (id: string): void => {
  const cleanId = id.trim();
  if (cleanId) {
    safeLocalStorage.setItem('GA_MEASUREMENT_ID', cleanId);
  } else {
    safeLocalStorage.removeItem('GA_MEASUREMENT_ID');
  }
  if (typeof window !== 'undefined') {
    window.GA_MEASUREMENT_ID = cleanId;
    const scripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]');
    scripts.forEach(script => script.remove());
  }

  isInitialized = false;
  currentActiveMeasurementId = '';
  initGA(true);
};

export const updateGAConsent = (granted: boolean): void => {
  if (typeof window === 'undefined') return;
  const status = granted ? 'granted' : 'denied';
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: status,
      ad_storage: status,
      ad_user_data: status,
      ad_personalization: status,
    });
  }
};

export const setGAUserId = (userId: string | number | null): void => {
  if (typeof window === 'undefined') return;
  if (!isInitialized) initGA();

  const idString = userId ? String(userId) : undefined;
  try {
    if (idString) {
      ReactGA.set({ user_id: idString });
      if (typeof window.gtag === 'function') {
        window.gtag('set', 'user_properties', { user_id: idString });
      }
    } else {
      ReactGA.set({ user_id: null });
      if (typeof window.gtag === 'function') {
        window.gtag('set', 'user_properties', { user_id: null });
      }
    }
  } catch (err) {
    console.warn('[GA4] Ошибка установки user_id:', err);
  }
};

export const sendPageView = (path: string, title?: string): void => {
  if (!isInitialized) initGA();
  const pageTitle = title || (typeof document !== 'undefined' ? document.title : '');
  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: pageTitle,
    });
  } catch (err) {
    console.warn('[GA4] Ошибка отправки pageview через react-ga4:', err);
  }
};

export interface GAEventOptions {
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

const sanitizeEventName = (rawName: string): string => {
  return rawName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 40);
};

const sanitizeParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === null) continue;
    const cleanKey = key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40);
    if (typeof val === 'string') {
      result[cleanKey] = val.slice(0, 100);
    } else if (typeof val === 'number' || typeof val === 'boolean') {
      result[cleanKey] = val;
    } else if (typeof val === 'object') {
      result[cleanKey] = JSON.stringify(val).slice(0, 100);
    }
  }
  return result;
};

export const sendAnalyticsEvent = (
  optionsOrName: GAEventOptions | string,
  maybeParams?: Record<string, unknown>
): void => {
  if (!isInitialized) initGA();

  let eventName = '';
  let eventParams: Record<string, unknown> = {};

  if (typeof optionsOrName === 'string') {
    eventName = optionsOrName;
    if (maybeParams && typeof maybeParams === 'object') {
      eventParams = { ...maybeParams };
    }
  } else if (optionsOrName && typeof optionsOrName === 'object') {
    const { action, category, label, value, ...rest } = optionsOrName;
    eventName = action || 'custom_event';
    if (category) eventParams.event_category = category;
    if (label) eventParams.event_label = label;
    if (value !== undefined) eventParams.value = value;
    Object.assign(eventParams, rest);
  }

  const cleanEventName = sanitizeEventName(eventName);
  const cleanParams = sanitizeParams(eventParams);

  try {
    ReactGA.event(cleanEventName, cleanParams);
  } catch (err) {
    console.warn('[GA4] Ошибка отправки события через react-ga4:', err);
  }
};

export default {
  initGA,
  getGAMeasurementId,
  isRealGAMeasurementId,
  setGAMeasurementId,
  updateGAConsent,
  setGAUserId,
  sendPageView,
  sendAnalyticsEvent,
};
